/* CBRNMASKS educational models, version 2.0.
 * Every equation and data source is documented in MODEL-NOTES.md.
 * Pure functions only: no DOM access, so the same file runs in the browser and in Node tests.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.CBRNModels = factory();
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  /* ---------------------------------------------------------------- helpers */
  const EARTH_KM = 6371;
  const MI = 1.609344;
  const clamp = (n, min, max) => Math.min(max, Math.max(min, Number.isFinite(+n) ? +n : min));
  const rad = d => d * Math.PI / 180;
  const deg = r => r * 180 / Math.PI;

  function destination(lat, lon, km, bearing) {
    const d = km / EARTH_KM, p = rad(lat), l = rad(lon), b = rad(bearing);
    const p2 = Math.asin(Math.sin(p) * Math.cos(d) + Math.cos(p) * Math.sin(d) * Math.cos(b));
    const l2 = l + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(p), Math.cos(d) - Math.sin(p) * Math.sin(p2));
    return [deg(p2), ((deg(l2) + 540) % 360) - 180];
  }
  function distance(a, b) {
    const p = rad(b[0] - a[0]), l = rad(b[1] - a[1]);
    return EARTH_KM * 2 * Math.asin(Math.min(1, Math.sqrt(Math.sin(p / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(l / 2) ** 2)));
  }
  /* Initial great-circle bearing from a to b, degrees clockwise from north. */
  function bearing(a, b) {
    const p1 = rad(a[0]), p2 = rad(b[0]), dl = rad(b[1] - a[1]);
    const y = Math.sin(dl) * Math.cos(p2), x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
    return (deg(Math.atan2(y, x)) + 360) % 360;
  }
  /* Position of a point relative to an origin and a downwind bearing.
   * x = km along the bearing, y = km to the right of it. */
  function toLocal(origin, brg, point) {
    const d = distance(origin, point), b = rad(bearing(origin, point) - brg);
    return [d * Math.cos(b), d * Math.sin(b)];
  }
  function fromLocal(origin, brg, x, y) {
    return destination(origin[0], origin[1], Math.hypot(x, y), brg + deg(Math.atan2(y, x)));
  }
  /* Ellipse with its rear edge `back` km behind the origin and front edge `forward` km ahead. */
  function ellipse(lat, lon, forward, back, width, brg, steps = 96) {
    const out = [], a = (forward + back) / 2, shift = (forward - back) / 2;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps * Math.PI * 2;
      out.push(fromLocal([lat, lon], brg, shift + a * Math.cos(t), width * Math.sin(t)));
    }
    return out;
  }
  /* Piecewise-linear interpolation in log-log space, clamped to the table. */
  function logInterp(xs, ys, x) {
    if (x <= xs[0]) return ys[0] * (x / xs[0]) ** (Math.log(ys[1] / ys[0]) / Math.log(xs[1] / xs[0]));
    for (let i = 1; i < xs.length; i++) {
      if (x <= xs[i]) {
        const f = Math.log(x / xs[i - 1]) / Math.log(xs[i] / xs[i - 1]);
        return Math.exp(Math.log(ys[i - 1]) + f * Math.log(ys[i] / ys[i - 1]));
      }
    }
    const n = xs.length - 1;
    return ys[n] * (x / xs[n]) ** (Math.log(ys[n] / ys[n - 1]) / Math.log(xs[n] / xs[n - 1]));
  }
  /* Deterministic pseudo-random generator (mulberry32) for repeatable dot layouts. */
  function prng(seed) {
    let a = seed >>> 0;
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      let t = Math.imul(a ^ a >>> 15, 1 | a);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  /* ============================================================ NUCLEAR */
  /* Peak overpressure, H.L. Brode, "Airblast from Nuclear Bursts - Analytic Approximations"
   * (Pacific-Sierra Research, 1986), pp. 60-71, as implemented in GLASSTONE (MIT licence).
   * z = burst height / ground range, r = scaled slant range (kft/kt^1/3), y = scaled height. Result in psi. */
  function brode(z, r, y) {
    const a = 1.22 - ((3.908 * z ** 2) / (1 + 810.2 * z ** 5));
    const b = 2.321 + ((6.195 * z ** 18) / (1 + 1.113 * z ** 18)) - ((0.03831 * z ** 17) / (1 + 0.02415 * z ** 17)) + (0.6692 / (1 + 4164 * z ** 8));
    const c = 4.153 - ((1.149 * z ** 18) / (1 + 1.641 * z ** 18)) - (1.1 / (1 + 2.771 * z ** 2.5));
    const d = -4.166 + ((25.76 * z ** 1.75) / (1 + 1.382 * z ** 18)) + ((8.257 * z) / (1 + 3.219 * z));
    const e = 1 - ((0.004642 * z ** 18) / (1 + 0.003886 * z ** 18));
    const f = 0.6096 + ((2.879 * z ** 9.25) / (1 + 2.359 * z ** 14.5)) - ((17.5 * z ** 2) / (1 + 71.66 * z ** 3));
    const g = 1.83 + ((5.361 * z ** 2) / (1 + 0.3139 * z ** 6));
    const h = ((8.808 * z ** 1.5) / (1 + 154.5 * z ** 3.5)) - ((0.2905 + 64.67 * z ** 5) / (1 + 441.5 * z ** 5)) - ((1.389 * z) / (1 + 49.03 * z ** 5)) + ((1.094 * r ** 2) / ((781.2 - (123.4 * r) + (37.98 * r ** 1.5) + r ** 2) * (1 + (2 * y))));
    const j = ((0.000629 * y ** 4) / (3.493e-9 + y ** 4)) - ((2.67 * y ** 2) / (1 + (1e7 * y ** 4.3)));
    const k = 5.18 + ((0.2803 * y ** 3.5) / (3.788e-6 + y ** 4));
    return (10.47 / r ** a) + (b / r ** c) + ((d * e) / (1 + (f * r ** g))) + h + (j / r ** k);
  }
  const KFT = 0.3048; // km per kilofoot
  function overpressurePsi(yieldKt, groundKm, hobKm) {
    const s = Math.cbrt(yieldKt), gr = Math.max(groundKm, 1e-6) / KFT, hk = hobKm / KFT;
    const x = gr / s, y = hk / s;
    return brode(hk / gr, Math.hypot(x, y), y);
  }
  /* Outermost ground range (km) at which peak overpressure reaches `psi`. Returns 0 if never reached. */
  const psiCache = {};
  function rangeForPsi(psi, airburst) {
    // Ranges for 1 kt; all other yields follow exact cube-root scaling because the burst height is cube-root scaled.
    const key = psi + (airburst ? 'a' : 's');
    if (psiCache[key] !== undefined) return psiCache[key];
    const hob = airburst ? NUCLEAR.HOB_KM_PER_KT13 : 0;
    let lo = 0.005, last = 0;
    for (let i = 0; i <= 400; i++) {
      const r = 0.005 * Math.pow(10, i / 100); // 5 m to 50 km
      if (overpressurePsi(1, r, hob) >= psi) last = r;
    }
    if (!last) return (psiCache[key] = 0);
    lo = last; let hi = last * Math.pow(10, 0.01);
    for (let i = 0; i < 60; i++) {
      const mid = (lo + hi) / 2;
      if (overpressurePsi(1, mid, hob) >= psi) lo = mid; else hi = mid;
    }
    return (psiCache[key] = lo);
  }

  const NUCLEAR = {
    HOB_KM_PER_KT13: 0.235, // Hiroshima-like scaled burst height: about 580 m for 15 kt
    // Precomputed from GLASSTONE (see tools/precompute_tables.py). Ground ranges in km.
    yields: [1, 2, 3, 5, 7, 10, 15, 20, 30, 50, 70, 100, 150, 200, 300, 500, 700, 1000],
    thermal3_air: [0.6921, 0.9566, 1.127, 1.4028, 1.6405, 1.9267, 2.2674, 2.5469, 3.012, 3.6451, 4.1423, 4.7476, 6.093, 6.8865, 7.9938, 9.7661, 10.8953, 12.1708],
    thermal3_surface: [0.441, 0.6002, 0.7117, 0.9112, 1.0415, 1.2006, 1.4228, 1.6229, 1.9439, 2.3862, 2.7361, 3.1571, 3.669, 4.09, 4.7699, 5.7446, 6.4833, 7.4096],
    rad500_slant: [0.83, 0.9429, 1.0071, 1.1122, 1.1635, 1.2178, 1.291, 1.3429, 1.4684, 1.5748, 1.6721, 1.7753, 1.9178, 2.019, 2.1377, 2.2972, 2.394, 2.4966],
    // Glasstone & Dolan (1977) Table 9.93: unit-time (H+1) reference dose-rate contours, contact surface burst,
    // 15 mph effective wind. Columns: rad/h, downwind a,b; maximum width a,b; ground-zero width a,b (statute miles, W in kt).
    fallout: [
      [3000, 0.95, 0.45, 0.0076, 0.86, 0.026, 0.58],
      [1000, 1.8, 0.45, 0.036, 0.76, 0.060, 0.57],
      [300, 4.5, 0.45, 0.13, 0.66, 0.20, 0.48],
      [100, 8.9, 0.45, 0.38, 0.60, 0.39, 0.42],
      [30, 16, 0.45, 0.76, 0.56, 0.53, 0.41],
      [10, 24, 0.45, 1.4, 0.53, 0.68, 0.41],
      [3, 30, 0.45, 2.2, 0.50, 0.89, 0.41],
      [1, 40, 0.45, 3.3, 0.48, 1.5, 0.41]
    ],
    levels: [1000, 100, 10, 1]
  };

  /* Glasstone & Dolan 9.93 wind factor for downwind distance; valid for about 8-45 mph. */
  function falloutWindFactor(windKmh) {
    const v = clamp(windKmh / MI, 8, 45);
    return v >= 15 ? 1 + (v - 15) / 60 : 1 + (v - 15) / 30;
  }
  /* Dimensions (km) of the H+1 dose-rate contour `level` (rad/h) for total yield W (kt) and fission fraction ff. */
  function falloutContour(W, level, ff, windKmh) {
    const tableLevel = level / ff; // dose-rate values in the table refer to the fission yield
    const T = NUCLEAR.fallout;
    if (tableLevel > T[0][0] || tableLevel < T[T.length - 1][0]) return null;
    let i = 0;
    while (i < T.length - 2 && tableLevel < T[i + 1][0]) i++;
    const A = T[i], B = T[i + 1];
    const f = Math.log(tableLevel / A[0]) / Math.log(B[0] / A[0]);
    const dims = row => [row[1] * W ** row[2], row[3] * W ** row[4], row[5] * W ** row[6]];
    const da = dims(A), db = dims(B);
    const lerp = (p, q) => Math.exp(Math.log(p) + f * Math.log(q / p));
    const downwind = lerp(da[0], db[0]) * falloutWindFactor(windKmh) * MI;
    const maxWidth = lerp(da[1], db[1]) * MI, gzWidth = lerp(da[2], db[2]) * MI;
    return { level, downwind, maxWidth, gzWidth, upwind: gzWidth / 2 };
  }
  function insideContour(c, x, y) {
    const a = c.gzWidth / 2;
    if (x * x + y * y <= a * a) return true;
    const cx = c.downwind / 2, A = c.downwind / 2, Bm = c.maxWidth / 2;
    return ((x - cx) / A) ** 2 + (y / Bm) ** 2 <= 1;
  }
  /* Outline of the idealized contour: a circle of ground-zero width around ground zero
   * merged with an ellipse that reaches the downwind distance with the stated maximum width. */
  function contourOutline(c, steps = 180) {
    const a = c.gzWidth / 2, cx = c.downwind / 2, A = c.downwind / 2, Bm = c.maxWidth / 2;
    const px = Math.min(a, c.downwind) / 2, py = 0, out = [];
    for (let i = 0; i <= steps; i++) {
      const th = i / steps * Math.PI * 2, dx = Math.cos(th), dy = Math.sin(th);
      const pd = px * dx + py * dy, pp = px * px + py * py;
      const tc = -pd + Math.sqrt(Math.max(0, pd * pd - (pp - a * a)));
      const qa = (dx / A) ** 2 + (dy / Bm) ** 2, qb = 2 * ((px - cx) * dx / (A * A) + py * dy / (Bm * Bm)), qc = ((px - cx) / A) ** 2 + (py / Bm) ** 2 - 1;
      const te = (-qb + Math.sqrt(Math.max(0, qb * qb - 4 * qa * qc))) / (2 * qa);
      const t = Math.max(tc, te);
      out.push([px + t * dx, py + t * dy]);
    }
    return out;
  }
  /* Way-Wigner decay used by Glasstone & Dolan: R(t) = R1 * t^-1.2 (t in hours after detonation). */
  const doseRateAt = (r1, t) => r1 * Math.pow(Math.max(t, 0.1), -1.2);
  /* Accumulated dose between ta and tb hours: 5 * R1 * (ta^-0.2 - tb^-0.2). */
  const accumulatedDose = (r1, ta, tb) => 5 * r1 * (Math.pow(ta, -0.2) - (Number.isFinite(tb) ? Math.pow(tb, -0.2) : 0));

  function nuclear(params) {
    const W = clamp(params.yieldKt, 1, 100000);
    const air = params.burst === 'air';
    const ff = clamp(params.fission ?? 0.5, 0.2, 1);
    const wind = clamp(params.wind ?? 20, 5, 100);
    const s = Math.cbrt(W), hob = air ? NUCLEAR.HOB_KM_PER_KT13 * s : 0;
    const fireball = (air ? 0.06096 : 0.0884) * W ** 0.4; // 2 x breakaway radius, Glasstone & Dolan 2.127
    const psi20 = rangeForPsi(20, air) * s, psi5 = rangeForPsi(5, air) * s, psi1 = rangeForPsi(1, air) * s;
    const thermal = logInterp(NUCLEAR.yields, air ? NUCLEAR.thermal3_air : NUCLEAR.thermal3_surface, W);
    const slant = logInterp(NUCLEAR.yields, NUCLEAR.rad500_slant, W);
    const radiation = Math.sqrt(Math.max(0, slant * slant - hob * hob));
    let fallout = [];
    if (!air) fallout = NUCLEAR.levels.map(l => falloutContour(W, l, ff, wind)).filter(Boolean);
    return { yieldKt: W, air, hobKm: hob, fission: ff, wind, fireball, psi20, psi5, psi1, thermal, radiation, fallout,
      falloutMaxKm: fallout.length ? Math.max(...fallout.map(c => c.downwind)) : 0 };
  }
  /* Fallout at a local point (x downwind, y crosswind, km). */
  function falloutAt(result, x, y) {
    let level = 0;
    for (const c of result.fallout) if (insideContour(c, x, y) && c.level > level) level = c.level;
    if (!level) return null;
    const arrival = Math.max(0.5, x / Math.max(result.wind, 1)); // hours; WSEG-10 uses a 0.5 h minimum
    return { level, arrival, onArrival: doseRateAt(level, arrival), after48h: doseRateAt(level, Math.max(48, arrival)) };
  }

  /* ============================================================ REACTOR */
  /* Pasquill stability class (table reproduced from Turner via UNBC course notes; see MODEL-NOTES). */
  function stabilityClass(windKmh, sky) {
    const v = windKmh / 3.6;
    if (sky === 'overcast') return 'D';
    if (sky === 'night') return v < 3 ? 'F' : v < 4 ? 'E' : 'D';
    return v < 2 ? 'A' : v < 3 ? 'AB' : v < 4 ? 'B' : 'C';
  }
  /* Briggs (1973) open-country horizontal dispersion: sigma_y = a x (1 + 0.0001 x)^-1/2, x in metres. */
  const BRIGGS_A = { A: 0.22, B: 0.16, C: 0.11, D: 0.08, E: 0.06, F: 0.04 };
  function sigmaY(xKm, cls) {
    const x = Math.max(xKm, 0) * 1000;
    const a = cls.length === 2 ? (BRIGGS_A[cls[0]] + BRIGGS_A[cls[1]]) / 2 : BRIGGS_A[cls];
    return a * x * Math.pow(1 + 0.0001 * x, -0.5) / 1000;
  }
  /* A continuous release travelling with a constant wind. Returns the travelling segment and its width.
   * `sigmas` = half-width in multiples of sigma_y (2 => about 95% of the material crosswind). */
  function plume(params, hours) {
    const wind = clamp(params.wind, 1, 100), duration = clamp(params.duration ?? 1, 0.1, 48);
    const t = clamp(hours, 0, 72), cls = stabilityClass(wind, params.sky || 'overcast');
    const front = wind * t, tail = wind * Math.max(0, t - duration);
    return { wind, duration, cls, front, tail, emitting: t < duration, hours: t,
      halfWidth: (x, sigmas = 2) => sigmas * sigmaY(Math.min(Math.max(x, 0), front), cls) };
  }
  function plumeOutline(p, sigmas = 2, steps = 60) {
    if (p.front <= 0) return [];
    const top = [], bottom = [], x0 = p.tail, x1 = p.front;
    for (let i = 0; i <= steps; i++) {
      const x = x0 + (x1 - x0) * Math.pow(i / steps, 1.6);
      const w = Math.max(p.halfWidth(x, sigmas), x0 > 0 ? 0.05 : 0);
      top.push([x, w]); bottom.push([x, -w]);
    }
    const cap = [], wf = p.halfWidth(x1, sigmas);
    for (let i = 1; i < 12; i++) { const th = -Math.PI / 2 + i / 12 * Math.PI; cap.push([x1 + 0.35 * wf * Math.cos(th), -wf * Math.sin(th)]); }
    const back = [];
    if (x0 > 0) { const wb = p.halfWidth(x0, sigmas); for (let i = 1; i < 12; i++) { const th = Math.PI / 2 + i / 12 * Math.PI; back.push([x0 + 0.35 * wb * Math.cos(th), wb * Math.sin(th)]); } }
    return [...top, ...cap, ...bottom.reverse(), ...back];
  }
  function inPlume(p, x, y) { return x >= p.tail && x <= p.front && Math.abs(y) <= p.halfWidth(x, 2); }

  /* ============================================================ WILDFIRE */
  /* Head-fire rate of spread (km/h). U = 10 m open wind speed (km/h), M = dead fine fuel moisture (%).
   * Grassland: CSIRO grassland model, natural pasture, fully cured (Cheney et al. 1998).
   * Woodland: grassland rate x 0.5 (Cheney & Sullivan 2008). Shrubland: Anderson et al. (2015), WF 0.67, 1 m height. */
  function headRos(fuel, U, M) {
    if (fuel === 'shrub') {
      const WF = 0.67, R0 = 5, h = 1.0;
      const base = U < 5 ? (R0 + 0.2 * (5.67 * Math.pow(5 * WF, 0.91) - R0) * U) : 5.67 * Math.pow(WF * U, 0.91);
      return base * Math.pow(h, 0.22) * Math.exp(-0.076 * M) * 0.06;
    }
    const base = U < 5 ? 0.054 + 0.269 * U : 1.4 + 0.838 * Math.pow(U - 5, 0.844);
    const phiM = M <= 12 ? Math.exp(-0.108 * M) : (U <= 10 ? 0.684 - 0.0342 * M : 0.547 - 0.0228 * M);
    return Math.max(0, base * Math.max(0, phiM)) * (fuel === 'woodland' ? 0.5 : 1);
  }
  function wildfire(params, hours) {
    const t = clamp(hours, 0, 48), U = clamp(params.wind, 0, 80), M = clamp(params.moisture, 2, 25);
    const fuel = ['grass', 'woodland', 'shrub'].includes(params.fuel) ? params.fuel : 'shrub';
    const ros = headRos(fuel, U, M);
    const lbr = U < 5 ? 1 : 1.1 * Math.pow(U, 0.464); // length-to-breadth ratio (Taylor 1997, via CSIRO)
    const root = Math.sqrt(Math.max(lbr * lbr - 1, 0));
    const hbr = lbr > 1 ? (lbr + root) / (lbr - root) : 1; // head-to-back ratio (Alexander 1985)
    const head = ros * t, back = head / hbr, length = head + back, halfWidth = length / (2 * lbr);
    return { fuel, ros, lbr, head, back, halfWidth, area: Math.PI * (length / 2) * halfWidth, smoke: U * Math.min(t, 6) };
  }

  /* ============================================================ OUTBREAK */
  /* Deterministic SIR, 0.1-day Euler steps, closed population. */
  function outbreak(params) {
    const N = Math.round(clamp(params.population, 1000, 40000000));
    const initial = Math.min(N, Math.round(clamp(params.initial, 1, 500)));
    const recovery = clamp(params.recovery, 3, 21), r0 = clamp(params.r0, 0.6, 4);
    const reduction = clamp(params.reduction, 0, 80) / 100, beta = r0 * (1 - reduction) / recovery;
    let S = N - initial, I = initial, R = 0; const history = [];
    let peak = I, peakDay = 0;
    for (let day = 0; day <= 120; day++) {
      history.push({ day, S, I, R }); if (I > peak) { peak = I; peakDay = day; }
      for (let j = 0; j < 10; j++) {
        const infected = Math.min(S, beta * S * I / N * 0.1), recovered = Math.min(I, I / recovery * 0.1);
        S -= infected; I += infected - recovered; R = N - S - I;
      }
    }
    return { history, N, peak, peakDay, effectiveR: r0 * (1 - reduction), totalInfected: N - history[120].S };
  }

  /* ---------------------------------------------------------- weather helpers */
  // Solar elevation angle in degrees (low-precision almanac formulas, US Naval Observatory; good to about 0.1° for 1950-2050).
  function solarElevation(lat, lon, date) {
    const rad = Math.PI / 180, d = date.getTime() / 86400000 - 10957.5;  // days since 2000-01-01 12:00 UTC
    const g = (357.529 + 0.98560028 * d) * rad, q = 280.459 + 0.98564736 * d;
    const L = (q + 1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g)) * rad, e = (23.439 - 0.00000036 * d) * rad;
    const dec = Math.asin(Math.sin(e) * Math.sin(L));
    const ra = Math.atan2(Math.cos(e) * Math.sin(L), Math.cos(L)) / rad;
    const gmst = ((18.697374558 + 24.06570982441908 * d) % 24 + 24) % 24;
    const ha = ((gmst * 15 + lon - ra) % 360 + 540) % 360 - 180;
    return Math.asin(Math.sin(lat * rad) * Math.sin(dec) + Math.cos(lat * rad) * Math.cos(dec) * Math.cos(ha * rad)) / rad;
  }
  // Sky setting used for the Pasquill class: clear sky with the sun above 15° counts as a sunny day,
  // clear sky with the sun below the horizon as a clear night, everything else (cloudy, dawn, dusk) as overcast.
  function skyFromWeather(cloudPercent, sunElevation) {
    const clear = Number.isFinite(cloudPercent) && cloudPercent < 50;
    if (clear && sunElevation > 15) return 'sunny';
    if (clear && sunElevation < 0) return 'night';
    return 'overcast';
  }
  // Fine dead fuel moisture (%) from air temperature (°C) and relative humidity (%): McArthur (1966),
  // as used with the CSIRO grassland fire spread model.
  function grassFuelMoisture(tempC, rh) { return 9.58 - 0.205 * tempC + 0.138 * rh; }

  return { clamp, destination, distance, bearing, toLocal, fromLocal, ellipse, logInterp, prng,
    brode, overpressurePsi, rangeForPsi, NUCLEAR, falloutWindFactor, falloutContour, insideContour, contourOutline,
    doseRateAt, accumulatedDose, nuclear, falloutAt,
    stabilityClass, sigmaY, plume, plumeOutline, inPlume,
    headRos, wildfire, outbreak, solarElevation, skyFromWeather, grassFuelMoisture };
});
